import { LinkOutlined, MessageOutlined, RightOutlined } from '@ant-design/icons';
import { IssueCaseDoc } from '@gooduncles/gu-app-schema';
import { Button, Popconfirm, Spin, message as antdMessage } from 'antd';
import dayjs from 'dayjs';
import { FC, MouseEvent, useCallback, useMemo, useState } from 'react';
import { useAuth } from 'src/stores/auth-context';

import {
  SelectedMessageRef,
  getMessageBodyPreview,
  linkMessagesToIssueCase,
  unlinkMessageFromIssueCase,
} from 'src/utils/issue/issue-case-message-link-util';

import useIssueCaseMessageLinks from 'src/hooks/subscription/useIssueCaseMessageLinks';
import useLinkedMessageDocs from 'src/hooks/swr/useLinkedMessageDocs';

import classes from './LinkedMessagesSection.module.scss';
import MessageLinkPickerModal from './MessageLinkPickerModal';
import SenderInfoPopover from './SenderInfoPopover';

interface LinkedMessagesSectionProps {
  issueCase: IssueCaseDoc;
  /** false 면 "+ 메시지 연결" / "연결 해제" 액션 숨김. 목록은 항상 노출. */
  editable?: boolean;
}

const LinkedMessagesSection: FC<LinkedMessagesSectionProps> = ({ issueCase, editable = true }) => {
  const { user } = useAuth();
  const { links, isLoading } = useIssueCaseMessageLinks(issueCase._id);
  const { messageDocs } = useLinkedMessageDocs(links);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const linkedMessageIds = useMemo(() => new Set(links.map((link) => link.messageId)), [links]);

  const handleConfirmPicker = useCallback(
    async (selected: SelectedMessageRef[], note: string | null) => {
      if (!user) {
        antdMessage.error('사용자 정보가 없습니다. 다시 로그인해주세요.');
        return;
      }
      const result = await linkMessagesToIssueCase(issueCase, selected, user._id, note);
      if (result.failureCount === 0) {
        antdMessage.success(`${result.successCount}건 연결 완료`);
        setPickerOpen(false);
      } else if (result.successCount === 0) {
        antdMessage.error(`연결 실패 (${result.failureCount}건). 다시 시도해주세요.`);
      } else {
        antdMessage.warning(`${result.successCount}건 연결 / ${result.failureCount}건 실패. 다시 시도해주세요.`);
        setPickerOpen(false);
      }
    },
    [issueCase, user],
  );

  const handleUnlink = useCallback(async (linkId: string) => {
    try {
      await unlinkMessageFromIssueCase(linkId);
      antdMessage.success('연결 해제 완료');
    } catch (error) {
      console.error('[LinkedMessagesSection] unlink failed', error);
      antdMessage.error('연결 해제에 실패했습니다.');
    }
  }, []);

  const handleAddClick = (e: MouseEvent) => {
    e.stopPropagation();
    setPickerOpen(true);
  };

  return (
    <>
      <div className={classes.headerToggle} onClick={() => setExpanded((prev) => !prev)} role='button' tabIndex={0}>
        <RightOutlined
          className={classes.chevron}
          style={{
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease-in-out',
          }}
        />
        <span className={classes.headerLabel}>연결 메시지</span>
        <span className={classes.headerCount}>{links.length}건</span>
        {editable && (
          <Button type='link' size='small' icon={<LinkOutlined />} onClick={handleAddClick}>
            추가
          </Button>
        )}
      </div>
      {expanded &&
        (isLoading ? (
          <div className={classes.spinner}>
            <Spin size='small' />
          </div>
        ) : links.length === 0 ? (
          <div className={classes.empty}>
            <MessageOutlined /> 연결된 메시지가 없습니다.
          </div>
        ) : (
          <div className={classes.list}>
            {links.map((link) => {
              const doc = messageDocs[link.messageId];
              const title = doc?.title ?? null;

              let bodyText: string;
              if (doc === null) {
                bodyText = '(메시지를 찾을 수 없습니다)';
              } else if (doc === undefined) {
                bodyText = '(불러오는 중…)';
              } else {
                bodyText = getMessageBodyPreview(doc.body ?? null, 200) || '(본문 없음)';
              }

              return (
                <div key={link._id} className={classes.row}>
                  <div className={classes.rowMain}>
                    {title && <div className={classes.title}>{title}</div>}
                    <div className={classes.body}>{bodyText}</div>
                    {link.note && <div className={classes.note}>📝 {link.note}</div>}
                    <div className={classes.rowFooter}>
                      <span className={classes.createdAt}>
                        {dayjs(link.messageCreatedAt).format('YYYY-MM-DD HH:mm')}
                      </span>
                      {doc?.senderCollection && doc?.senderId && (
                        <SenderInfoPopover label='보낸이' collection={doc.senderCollection} id={doc.senderId} />
                      )}
                      <SenderInfoPopover label='등록자' collection='user' id={link.createdBy} />
                    </div>
                  </div>
                  {editable && (
                    <Popconfirm
                      title='연결을 해제하시겠습니까?'
                      okText='해제'
                      cancelText='취소'
                      onConfirm={() => handleUnlink(link._id)}
                    >
                      <Button type='text' size='small' danger onClick={(e) => e.stopPropagation()}>
                        해제
                      </Button>
                    </Popconfirm>
                  )}
                </div>
              );
            })}
          </div>
        ))}

      <MessageLinkPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        storeId={issueCase.storeId}
        storeName={issueCase.storeName}
        alreadyLinkedMessageIds={linkedMessageIds}
        onConfirm={handleConfirmPicker}
      />
    </>
  );
};

export default LinkedMessagesSection;
